// Assign/edit-tag dialog. Assign: pick a kind (worker / manager / visitor /
// hoist / scissors lift) and an unassigned tag, then the kind's fields;
// worker and manager tags can carry certificates. Edit (worker / manager /
// scissors-lift tags): kind + tag locked — manager tags additionally lock
// the manager, leaving certificates as the only edit — fields prefilled,
// submit enabled only on change. Empty catalogs link to the sibling admin
// tabs; the hoist kind needs a hoist floor (support-provisioned).

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { IProfession } from '@d-orca/shared/trusstor';
import type { IAdminTag } from '@interfaces/IAdminTag';
import type { AssignTagInput, ITagAssignCertificate } from '@apis/adminTags';
import { Modal } from '@components/ui/Modal/Modal';
import { Button } from '@components/ui/Button/Button';
import { Field } from '@components/ui/Field/Field';
import { SingleSelect } from '@components/ui/SingleSelect/SingleSelect';
import { GroupedSelect } from '@components/ui/GroupedSelect/GroupedSelect';
import { PhoneField } from '@components/ui/PhoneField/PhoneField';
import type {
  IGroupedOptions,
  ISelectionOption,
} from '@components/Pages/AnalyticsPage/analytics.types';
import { professionDisplayText, tradeGroupLabel } from '@utils/professionDisplay';
import { APP_DEFAULT_TZ, formatYmd } from '@utils/dateUtils';
import { isTagAssigned } from '../utils/tagFilters';
import { useHoistAvailability, useTagCertificatesCatalog } from '../hooks/useAssignTag';
import { CertificateSection } from './CertificateSection/CertificateSection';
import { useAdminManagers } from '../../ManagementTab/hooks/useAdminManagers';
import styles from './AssignTagDialog.module.css';

type AssignKind = 'worker' | 'manager' | 'guest' | 'hoist' | 'scissorsLift';

const KINDS: { value: AssignKind; labelKey: string }[] = [
  { value: 'worker', labelKey: 'adminTags.assign.kind.worker' },
  { value: 'manager', labelKey: 'adminTags.assign.kind.manager' },
  { value: 'guest', labelKey: 'adminTags.assign.kind.guest' },
  { value: 'hoist', labelKey: 'adminTags.assign.kind.hoist' },
  { value: 'scissorsLift', labelKey: 'adminTags.assign.kind.scissorsLift' },
];

/** "Trade, specialty - contractor" groups for the profession picker — the
 *  bare site catalog, grouped by trade group. */
function professionGroups(
  t: ReturnType<typeof useTranslation>['t'],
  professions: IProfession[],
): IGroupedOptions[] {
  const sorted = [...professions]
    // The guest system trade is a kind of its own, not a pickable profession.
    .filter((p) => p.tradeId !== 'guest')
    .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0) || a.tradeId.localeCompare(b.tradeId));
  const byGroup = new Map<string, IProfession[]>();
  for (const profession of sorted) {
    const group = profession.tradeGroup ?? 'others';
    const bucket = byGroup.get(group);
    if (bucket) bucket.push(profession);
    else byGroup.set(group, [profession]);
  }
  return [...byGroup.entries()].map(([groupId, list]) => ({
    groupId,
    groupLabel: tradeGroupLabel(t, groupId),
    options: list.map((p) => ({ id: p._id, label: professionDisplayText(t, p) })),
  }));
}

export interface IAssignTagDraft {
  tagId: string;
  input: AssignTagInput;
}

interface IAssignTagDialogProps {
  open: boolean;
  /** Preselected tag (row action) — null for the toolbar flow. An ASSIGNED
   *  tag switches the dialog to edit mode (worker tags only reach it). */
  tag: IAdminTag | null;
  /** Unassigned tags only — an assigned tag must be unassigned first. */
  unassignedTags: IAdminTag[];
  professions: IProfession[];
  siteId: string;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (draft: IAssignTagDraft) => void;
}

export function AssignTagDialog({
  open,
  tag,
  unassignedTags,
  professions,
  siteId,
  submitting,
  onClose,
  onSubmit,
}: IAssignTagDialogProps) {
  const { t } = useTranslation();
  const isEdit = tag !== null && isTagAssigned(tag);

  // Edit prefills. `fullName` falls back to the tag nick for nameless
  // workers — don't let that fallback masquerade as a real name.
  const isScissorsEdit = isEdit && tag.kind === 'utility' && tag.linkedUtility?.tradeId === 'scissorsLift';
  const initialKind: AssignKind =
    isEdit && tag.kind === 'manager' ? 'manager' : isScissorsEdit ? 'scissorsLift' : 'worker';
  const initialProfessionId = isScissorsEdit
    ? (tag.linkedUtility?.professionId ?? null)
    : isEdit
      ? (tag.profession?.id ?? null)
      : null;
  const initialManagerId = isEdit ? (tag.manager?.id ?? null) : null;
  const initialName = isScissorsEdit
    ? (tag.linkedUtility?.name ?? '')
    : isEdit && tag.kind === 'worker' && tag.fullName !== (tag.tagNick ?? tag.tagId)
      ? tag.fullName
      : '';
  const initialIdNumber = isEdit ? (tag.idNumber ?? '') : '';
  const initialPhone = isEdit && tag.kind === 'worker' ? (tag.phoneNumber ?? '') : '';
  // Existing certificates round-trip with their creationDate (upstream
  // audit-log field); rows with no expiration can't ride the write shape.
  const initialCertificates = useMemo<ITagAssignCertificate[]>(
    () =>
      (isEdit ? tag.certificates : [])
        .filter((c) => c.expirationDate !== null)
        .map((c) => ({
          name: c.name,
          // JST calendar date, NOT a naive UTC slice — a JST-midnight instant
          // serializes with a UTC date one day earlier.
          expirationDate: formatYmd(c.expirationDate!, APP_DEFAULT_TZ),
          ...(c.creationDate !== null ? { creationDate: c.creationDate } : {}),
        })),
    [isEdit, tag],
  );
  const [kind, setKind] = useState<AssignKind>(initialKind);
  const [tagId, setTagId] = useState<string | null>(tag?.tagId ?? null);
  const [professionId, setProfessionId] = useState<string | null>(initialProfessionId);
  const [managerId, setManagerId] = useState<string | null>(initialManagerId);
  const [name, setName] = useState(initialName);
  const [idNumber, setIdNumber] = useState(initialIdNumber);
  const [phone, setPhone] = useState(initialPhone);
  const [certificates, setCertificates] = useState<ITagAssignCertificate[]>(initialCertificates);
  const [phoneHasError, setPhoneHasError] = useState(false);

  // The manager catalog loads lazily with the dialog (same data the
  // Management tab shows); worker professions arrive via props (already on
  // the page for the filter sidebar).
  const { data: managersData } = useAdminManagers(open && kind === 'manager' ? siteId : null);
  const managers = useMemo(() => managersData?.managers ?? [], [managersData]);

  const kindOptions = useMemo<ISelectionOption[]>(
    () => KINDS.map((k) => ({ id: k.value, label: t(k.labelKey) })),
    [t],
  );
  const tagOptions = useMemo<ISelectionOption[]>(
    () =>
      (tag ? [tag] : unassignedTags).map((row) => ({
        id: row.tagId,
        label: row.tagNick ?? row.tagId,
      })),
    [tag, unassignedTags],
  );
  const groups = useMemo(() => professionGroups(t, professions), [t, professions]);
  const hasProfessions = groups.some((g) => g.options.length > 0);
  const managerOptions = useMemo<ISelectionOption[]>(() => {
    const options: ISelectionOption[] = managers.map((m) => ({ id: m.id, label: m.name }));
    // Edit mode: the locked select must show the current manager even before
    // (or without) the catalog load.
    if (isEdit && tag.manager?.id && !options.some((o) => o.id === tag.manager!.id)) {
      options.unshift({ id: tag.manager.id, label: tag.manager.name });
    }
    return options;
  }, [managers, isEdit, tag]);

  // Certificate-name catalog — loads with the dialog for person kinds.
  const { data: certificatesData } = useTagCertificatesCatalog(
    open && (kind === 'worker' || kind === 'manager') ? siteId : null,
  );
  const certificatesCatalog = useMemo(
    () => certificatesData?.certificates ?? [],
    [certificatesData],
  );

  // Hoist gate — a floor flagged as the hoist must exist (support-provisioned).
  const { data: hoistData } = useHoistAvailability(open && kind === 'hoist' ? siteId : null);
  const hasHoistFloor = hoistData?.hasHoistFloor === true;

  const kindReady =
    kind === 'worker'
      ? professionId !== null
      : kind === 'manager'
        ? managerId !== null
        : kind === 'hoist'
          ? hasHoistFloor
          : kind === 'scissorsLift'
            ? professionId !== null && name.trim() !== ''
            : true;
  // Edit submits only when something actually changed.
  const changed =
    !isEdit ||
    professionId !== initialProfessionId ||
    name !== initialName ||
    idNumber !== initialIdNumber ||
    phone !== initialPhone ||
    JSON.stringify(certificates) !== JSON.stringify(initialCertificates);
  // Only the worker kind renders the phone field; a latched error from a
  // since-switched kind must not block submitting the visible form.
  const phoneBlocked = kind === 'worker' && phoneHasError;
  const canSubmit = tagId !== null && kindReady && changed && !phoneBlocked && !submitting;

  function handleClose() {
    setKind(initialKind);
    setTagId(tag?.tagId ?? null);
    setProfessionId(initialProfessionId);
    setManagerId(initialManagerId);
    setName(initialName);
    setIdNumber(initialIdNumber);
    setPhone(initialPhone);
    setCertificates(initialCertificates);
    setPhoneHasError(false);
    onClose();
  }

  function buildInput(): AssignTagInput {
    if (kind === 'worker') {
      return {
        kind,
        professionId: professionId!,
        name: name.trim(),
        idNumber: idNumber.trim(),
        phone: phone.trim() || null,
        certificates,
      };
    }
    if (kind === 'manager') return { kind, managerId: managerId!, certificates };
    if (kind === 'hoist') return { kind: 'hoist' };
    if (kind === 'scissorsLift') {
      return { kind, professionId: professionId!, name: name.trim() };
    }
    return { kind: 'guest' };
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t(isEdit ? 'adminTags.assign.editTitle' : 'adminTags.assign.title')}
      footer={
        <>
          <span className={styles.footerSpacer} />
          <Button variant="secondary" size="md" onClick={handleClose}>
            {t('adminTags.assign.cancel')}
          </Button>
          <Button
            variant="primary"
            size="md"
            disabled={!canSubmit}
            onClick={() => onSubmit({ tagId: tagId!, input: buildInput() })}
          >
            {t(isEdit ? 'adminTags.assign.update' : 'adminTags.assign.submit')}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <div className={styles.topRow}>
          <div className={styles.formField}>
            <span className={styles.formLabel}>
              {t('adminTags.assign.type')}
              <span className={styles.requiredMark} aria-hidden="true">*</span>
            </span>
            <SingleSelect
              value={kind}
              onChange={(next) => setKind(next as AssignKind)}
              options={kindOptions}
              placeholder={t('adminTags.assign.type')}
              ariaLabel={t('adminTags.assign.type')}
              disabled={isEdit}
              elevated
            />
          </div>
          <div className={styles.formField}>
            <span className={styles.formLabel}>
              {t('adminTags.assign.tagId')}
              <span className={styles.requiredMark} aria-hidden="true">*</span>
            </span>
            <SingleSelect
              value={tagId}
              onChange={setTagId}
              options={tagOptions}
              placeholder={t('adminTags.assign.tagId')}
              ariaLabel={t('adminTags.assign.tagId')}
              disabled={tag !== null}
              elevated
            />
          </div>
        </div>

        {kind === 'worker' &&
          (hasProfessions ? (
            <>
              <div className={styles.formField}>
                <span className={styles.formLabel}>
                  {t('adminTags.assign.profession')}
                  <span className={styles.requiredMark} aria-hidden="true">*</span>
                </span>
                <GroupedSelect
                  value={professionId}
                  onChange={setProfessionId}
                  groups={groups}
                  placeholder={t('adminTags.assign.profession')}
                  ariaLabel={t('adminTags.assign.profession')}
                  elevated
                />
              </div>
              <Field
                label={t('adminTags.assign.fullName')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
              />
              <Field
                label={t('adminTags.assign.idNumber')}
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value)}
                maxLength={100}
              />
              <PhoneField
                label={t('adminTags.assign.phone')}
                value={phone}
                onChange={setPhone}
                invalidMessage={t('adminTags.assign.phoneInvalid')}
                onErrorChange={setPhoneHasError}
              />
              <CertificateSection
                certificates={certificates}
                onChange={setCertificates}
                catalog={certificatesCatalog}
              />
            </>
          ) : (
            <p className={styles.emptyHint}>
              {t('adminTags.assign.noProfessions')}{' '}
              <Link className={styles.hintLink} to="?tab=professions">
                {t('adminTags.assign.goToProfessions')}
              </Link>
            </p>
          ))}

        {kind === 'hoist' && !hasHoistFloor && (
          <p className={styles.emptyHint}>
            {t('adminTags.assign.noHoist')} {t('adminTags.assign.noHoistContact')}
          </p>
        )}

        {kind === 'scissorsLift' &&
          (hasProfessions ? (
            <>
              <div className={styles.formField}>
                <span className={styles.formLabel}>
                  {t('adminTags.assign.profession')}
                  <span className={styles.requiredMark} aria-hidden="true">*</span>
                </span>
                <GroupedSelect
                  value={professionId}
                  onChange={setProfessionId}
                  groups={groups}
                  placeholder={t('adminTags.assign.profession')}
                  ariaLabel={t('adminTags.assign.profession')}
                  elevated
                />
              </div>
              <Field
                label={t('adminTags.assign.nickname')}
                requiredMark
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={200}
              />
            </>
          ) : (
            <p className={styles.emptyHint}>
              {t('adminTags.assign.noProfessions')}{' '}
              <Link className={styles.hintLink} to="?tab=professions">
                {t('adminTags.assign.goToProfessions')}
              </Link>
            </p>
          ))}

        {kind === 'manager' &&
          (managerOptions.length > 0 ? (
            <>
              <div className={styles.formField}>
                <span className={styles.formLabel}>
                  {t('adminTags.assign.manager')}
                  <span className={styles.requiredMark} aria-hidden="true">*</span>
                </span>
                <SingleSelect
                  value={managerId}
                  onChange={setManagerId}
                  options={managerOptions}
                  placeholder={t('adminTags.assign.manager')}
                  ariaLabel={t('adminTags.assign.manager')}
                  disabled={isEdit}
                  elevated
                />
              </div>
              <CertificateSection
                certificates={certificates}
                onChange={setCertificates}
                catalog={certificatesCatalog}
              />
            </>
          ) : (
            <p className={styles.emptyHint}>
              {t('adminTags.assign.noManagers')}{' '}
              <Link className={styles.hintLink} to="?tab=management">
                {t('adminTags.assign.goToManagement')}
              </Link>
            </p>
          ))}
      </div>
    </Modal>
  );
}